import { useCallback, useEffect, useRef, useState } from 'react';

const IDLE = Object.freeze({ status: 'idle', data: null, error: null, key: null });

/**
 * One request per `key`, answered only for that key.
 *
 * The same guarantees useCartQuote gives the cart, for requests that pin their
 * own geography (and so cannot rely on the client's stale-location check):
 *   - `key` null → idle, nothing requested;
 *   - a new key drops the previous answer IN THE SAME RENDER — state for any
 *     other key is never returned, not even while the new request is starting;
 *   - only the latest request may write, so a late answer for an old key is
 *     discarded;
 *   - `refresh()` asks again for the current key;
 *   - `replace(forKey, data)` installs an answer obtained elsewhere (e.g. the
 *     fresh quote a placement conflict returns) — only if `forKey` is still the
 *     current key, and so that no in-flight answer can overwrite it.
 *
 * `fetcher` is read at request time, so it always sees the inputs of the
 * render that produced the key.
 *
 * status: idle | loading | ready | error;  data: the response's `data`.
 */
const useKeyedRequest = (key, fetcher) => {
  const [state, setState] = useState(IDLE);
  const [attempt, setAttempt] = useState(0);
  const requestIdRef = useRef(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const keyRef = useRef(key);
  keyRef.current = key;

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    if (!key) {
      setState(IDLE);
      return undefined;
    }
    setState({ status: 'loading', data: null, error: null, key });
    Promise.resolve()
      .then(() => fetcherRef.current())
      .then((response) => {
        if (requestId !== requestIdRef.current) return;
        setState({ status: 'ready', data: response?.data ?? null, error: null, key });
      })
      .catch((error) => {
        if (requestId !== requestIdRef.current) return;
        setState({ status: 'error', data: null, error, key });
      });
    return () => {
      if (requestIdRef.current === requestId) requestIdRef.current += 1;
    };
  }, [key, attempt]);

  const refresh = useCallback(() => setAttempt((count) => count + 1), []);
  const replace = useCallback((forKey, data) => {
    if (!forKey || forKey !== keyRef.current) return false;
    requestIdRef.current += 1;
    setState({ status: 'ready', data, error: null, key: forKey });
    return true;
  }, []);
  const current = key && state.key === key ? state : { ...IDLE, status: key ? 'loading' : 'idle', key: key || null };
  return { status: current.status, data: current.status === 'ready' ? current.data : null, error: current.status === 'error' ? current.error : null, key: key || null, refresh, replace };
};

export default useKeyedRequest;
